import { isMatch, merge, pick } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useHistory, useParams } from 'react-router';

import usePersistent from 'hooks/use-persistent';
import { ROUTES } from 'pages/Search/constants';
import { noneIf } from 'pages/Search/utils';
import { safeDecodeUriComponent } from 'utils/safe-decode-uri-component';

import { Order } from '../constants';

interface State {
    type: string;
    order: Order;
    page: number;
}

const defaults: State = {
    type: '',
    order: Order.DATE,
    page: 1
};

export function useTypesCveCatalogState() {
    const history = useHistory();
    const parameters = useParams<{ type: string; order: Order; page: string }>();

    const type = parameters.type ? safeDecodeUriComponent(parameters.type) : defaults.type;
    const page = parameters.page ? parseInt(parameters.page) : defaults.page;
    const order = parameters.order ?? defaults.order;

    const state = useMemo(() => ({ type, page, order }), [order, page, type]);
    const payloadRef = usePersistent(state);

    const url = useCallback(
        (patch: Partial<State>) => {
            const previous = payloadRef.current;
            const current = merge({}, previous, patch);

            const path = ROUTES.TYPES_CVE({
                type: current.type,
                order: noneIf(current.order, Order.DATE),
                page: isMatch(previous, pick(current, ['vendor', 'product', 'year', 'month', 'score', 'size']))
                    ? noneIf(current.page, defaults.page)
                    : null
            });

            return path;
        },
        [payloadRef]
    );

    const set = useCallback((patch: Partial<State>) => history.push(url(patch)), [history, url]);

    const actions = useMemo(() => ({ url, set }), [set, url]);

    return [state, actions] as const;
}
